<template>
  <component
    :is="tagCompute"
    :id="id"
    :title="title"
    :aria-label="ariaLabel"
    :to="to"
    :href="href"
    :class="classCompute"
    @click="clickHandler"
  >
    <slot />
    <TickCircleIcon v-if="selected && showCheckmark" class="checkmark" aria-hidden="true" />
  </component>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import TickCircleIcon from '../../graphics/icons/TickCircleIcon.vue';
import { LinkTarget } from '../../helpers/redirects';

export default {
  name: 'Card',
  components: { TickCircleIcon, RouterLink },
  props: {
    id: {
      type: String,
      default: null,
    },
    elevation: {
      default: 2,
      type: Number,
    },
    clickable: {
      default: false,
      type: Boolean,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showCheckmark: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    href: {
      type: [String, Boolean],
      default: null,
    },
    to: {
      type: [String, Object] as PropType<RouteLocationRaw>,
      default: null,
    },
    target: {
      type: String as PropType<LinkTarget>,
      default: '_self',
    },
    customClasses: {
      type: String,
      default: '',
      required: false,
    },
    useFullPageNavigation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    // TODO: improve card accessibility + allow cards to be read as links (ticket story ID: 182027)
    tagCompute() {
      if (this.to) {
        return RouterLink;
      }
      if (this.clickable) {
        return 'button';
      }
      return 'div';
    },
    boxShadowClass(): string {
      // box shadow classes are for default appearance, elevation classes are for hover if clickable
      const elevationClassMap = {
        1: 'border-1 border-solid border-grey-divider-subdued elevation-1',
        2: 'box-shadow-sm elevation-2',
        3: 'box-shadow-md elevation-3',
        4: 'box-shadow-lg elevation-4',
      };

      const className = elevationClassMap[this.elevation] ? elevationClassMap[this.elevation] : '';

      return className;
    },
    classCompute(): String[] {
      const hasCustomBackground = /\bbg-/.test(this.customClasses || '');
      return [
        'card',
        'p-16', // keeping padding here makes it easier to override
        hasCustomBackground ? '' : 'bg-white',
        this.customClasses,
        this.clickable ? 'card-clickable' : '',
        this.boxShadowClass,
        this.selected ? 'selected' : '',
        this.disabled ? 'card-disabled' : '',
      ];
    },
  },
  methods: {
    isInternalRoute(href: string): boolean {
      return href.startsWith('/') && !href.startsWith('//') && !href.startsWith('http');
    },
    clickHandler(e) {
      if (this.disabled) {
        e.preventDefault();
        return false;
      }

      if (this.to) {
        if (this.clickable) {
          this.$emit('click', e);
        }
        return false;
      }
      if (!this.clickable) return false;

      e.preventDefault();

      if (this.href && typeof this.href === 'string') {
        if (this.useFullPageNavigation) {
          window.location.assign(this.href);
        } else if (this.isInternalRoute(this.href) && this.target === '_self') {
          this.$router.push(this.href);
        } else {
          window.open(this.href, this.target);
        }
      } else {
        this.$emit('click', e);
      }

      return false;
    },
  },
};
</script>

<style scoped lang="scss">
.card {
  box-sizing: border-box;
  text-align: left;
  position: relative;
  border-radius: 4px;
  outline: none;
  cursor: default;

  &.card-clickable {
    cursor: pointer;
    &:hover {
      &.elevation-1 {
        box-shadow: theme('boxShadow.sm');
      }
      &.elevation-2 {
        box-shadow: theme('boxShadow.md');
      }
      &.elevation-3 {
        box-shadow: theme('boxShadow.lg');
      }
      &.elevation-4 {
        box-shadow: theme('boxShadow.xl');
      }
    }
  }

  &.card-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.selected {
    .checkmark {
      position: absolute;
      bottom: 3px;
      right: 3px;
      background-color: theme('colors.foreground');
      border-radius: 50%;
    }
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: calc(100% + 4px);
      height: calc(100% + 4px);
      border: 3px solid theme('colors.primary.DEFAULT');
      margin: -2px;
      border-radius: 4px;
    }
  }
  &:focus-visible::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% + 8px);
    height: calc(100% + 8px);
    border: 2px dotted theme('colors.brand.contrast');
    margin: -4px;
    border-radius: 4px;
  }
}
</style>
